/* Text field primitive: the header's search pill AND form inputs.

   One component with `shape` / `multiline` variants rather than two primitives,
   because the information model is identical — a single editable text value.
   (Contrast item-tile vs item-row, which differ in *what information leads* and
   are therefore separate components. Variants for presentation, separate
   components for content models.)

   Emits a debounced `g-input` with the current value. Debouncing lives here so
   every consumer gets the same behaviour; v1 hand-rolled 220ms at the call site.
   Form fields pass debounce="0" for immediate feedback.

   The disabled state matters structurally: on a non-searchable workspace the
   field stays in place, dimmed and inert, so the header never reflows between
   workspaces (see the shell design). */

import { LitElement, html, css } from 'lit';
import { shadowReset } from '../../design/base.js';
import { customElement, property } from 'lit/decorators.js';

@customElement('g-field')
export class GField extends LitElement {
  static override styles = [shadowReset, css`
    :host {
      display: flex;
      flex: 1;
      min-width: 0;
    }
    /* form shape stacks an optional label above the control */
    :host([shape='box']) {
      display: block;
      flex: none;
    }
    .wrap {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      flex: 1;
      min-width: 0;
      padding: 0 var(--space-3);
      height: 36px;
      border: 1px solid var(--border);
      border-radius: var(--radius-full);
      background: var(--surface);
      color: var(--text-dim);
      transition: border-color 120ms ease, opacity 120ms ease;
    }
    .wrap:focus-within {
      border-color: var(--accent);
      box-shadow: var(--ring);
    }
    :host([disabled]) .wrap {
      opacity: 0.45;
      background: var(--surface-2);
    }
    /* form shape: squared corners, auto height so a textarea can grow */
    :host([shape='box']) .wrap {
      border-radius: var(--radius-md);
      height: auto;
      min-height: 34px;
      padding: var(--space-2) var(--space-3);
      align-items: start;
    }
    input,
    textarea {
      flex: 1;
      min-width: 0;
      border: 0;
      background: none;
      outline: none;
      color: var(--text);
      font: inherit;
      font-family: var(--font-ui);
      resize: vertical;
    }
    textarea {
      min-height: 3.2em;
      line-height: 1.45;
    }
    /* 16px is not a design choice — it is the threshold below which iOS Safari
       zooms the page when a field takes focus, and it does that in a standalone
       PWA too, where there is no visible way back out. The inherited size is
       15px, which is just under.

       Keyed on pointer:coarse rather than width: it is a property of the input
       method, and a narrow desktop window has no such behaviour. Desktop keeps
       the inherited size, so nothing about the header field changes there. */
    @media (pointer: coarse) {
      input,
      textarea {
        font-size: 16px;
      }
    }
    /* The pill draws its own clear button below, so the native one always goes —
       otherwise the field carries two ✕ side by side, one of them unstyled. Only
       WebKit/Blink render it at all. */
    input[type='search']::-webkit-search-cancel-button {
      display: none;
    }
    /* Inside the pill, at its trailing edge: clearing a search field is this
       component's own affordance, not something each caller reimplements. Present
       only when there is something to clear. */
    .clear {
      display: inline-flex;
      position: relative;
      flex: none;
      align-items: center;
      justify-content: center;
      width: 24px;
      height: 24px;
      padding: 0;
      border: 0;
      border-radius: var(--radius-full);
      background: none;
      color: var(--text-dim);
      cursor: pointer;
    }
    /* Guarded: on touch a hover latches and outlives the tap (see g-chips). */
    @media (hover: hover) {
      .clear:hover {
        color: var(--text);
      }
    }
    .clear:active {
      background: var(--press);
    }
    .clear g-icon {
      --icon-size: 16px;
    }
    /* 24px of paint, 44px of hit area. Vertical and RIGHTWARD only: the pill's own
       12px of end padding is dead space to grow into, while growing leftward would
       steal taps meant for the caret at the end of the text. */
    @media (pointer: coarse) {
      .clear::after {
        content: '';
        position: absolute;
        inset: -10px -6px -10px 0;
      }
    }
    input::placeholder,
    textarea::placeholder {
      color: var(--text-dim);
    }
    input:disabled,
    textarea:disabled {
      cursor: not-allowed;
    }
    label {
      display: block;
      margin-bottom: var(--space-1);
      color: var(--text-dim);
      font-size: 0.78rem;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
  `];

  @property() placeholder = '';
  @property() value = '';
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Number }) debounce = 220;
  /** `pill` = the header's search field; `box` = a form input. */
  @property({ reflect: true }) shape: 'pill' | 'box' = 'pill';
  @property({ type: Boolean }) multiline = false;
  /** Visible label, form fields only. */
  @property() label = '';
  /** Leading icon name; defaults to the search glyph on the pill shape. */
  @property() icon = '';

  private timer?: number;

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    clearTimeout(this.timer);
  }

  private onInput(e: Event): void {
    this.value = (e.target as HTMLInputElement | HTMLTextAreaElement).value;
    this.emit();
  }

  private emit(immediate = false): void {
    const fire = () =>
      this.dispatchEvent(
        new CustomEvent('g-input', { detail: this.value, bubbles: true, composed: true }),
      );
    clearTimeout(this.timer);
    if (immediate || this.debounce <= 0) fire();
    else this.timer = window.setTimeout(fire, this.debounce);
  }

  /**
   * Empty the field.
   *
   * Undebounced, unlike typing: a tap on ✕ is a finished instruction, not a pause
   * between keystrokes, and 220ms of stale results after it reads as a dropped tap.
   *
   * `g-clear` is separate from the empty `g-input` deliberately — a caller that
   * needs to distinguish "cleared it" from "backspaced to empty" cannot do so from
   * the value alone, and the mobile filter band closes on one but not the other.
   */
  private onClear = (): void => {
    this.value = '';
    this.emit(true);
    this.dispatchEvent(new CustomEvent('g-clear', { bubbles: true, composed: true }));
    // Focus stays put, so the field is ready for a new query rather than dismissed.
    this.renderRoot.querySelector('input')?.focus();
  };

  /* Hold focus through the tap and act on click: clearing re-renders the button out
     of existence, and acting on pointerdown would let the following click retarget
     to whatever takes its place. preventDefault on pointerdown suppresses the focus
     change but not the click. */
  private hold = (e: Event): void => {
    e.preventDefault();
  };

  override render() {
    const glyph = this.icon || (this.shape === 'pill' ? 'search' : '');
    const name = this.label || this.placeholder || 'Text';
    // Only the pill is a search field, and only a non-empty one has anything to clear.
    const clearable = this.shape === 'pill' && !this.disabled && this.value.length > 0;
    return html`
      ${this.label ? html`<label for="c">${this.label}</label>` : ''}
      <div class="wrap">
        <!-- Slotted content REPLACES the glyph rather than joining it, so a caller
             putting a real control in this position (the mobile band's back button)
             does not have to separately suppress the decorative one. -->
        <slot name="lead">
          ${glyph ? html`<g-icon name=${glyph} style="--icon-size:16px"></g-icon>` : ''}
        </slot>
        ${this.multiline
          ? html`<textarea
              id="c"
              .value=${this.value}
              placeholder=${this.placeholder}
              ?disabled=${this.disabled}
              @input=${this.onInput}
              aria-label=${name}
            ></textarea>`
          : html`<input
              id="c"
              type=${this.shape === 'pill' ? 'search' : 'text'}
              .value=${this.value}
              placeholder=${this.placeholder}
              ?disabled=${this.disabled}
              @input=${this.onInput}
              aria-label=${name}
            />`}
        ${clearable
          ? html`<button
              class="clear"
              aria-label="Clear"
              @pointerdown=${this.hold}
              @click=${this.onClear}
            >
              <g-icon name="x"></g-icon>
            </button>`
          : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-field': GField;
  }
}
